import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { PsychologicalAppealTypeModel } from 'forms/sections/psychological/shared/OrderType';
import Field from 'components/Form/Field';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import {
  CommonOnError,
  CommonOnUpdate,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from '../../../types/props';

interface Props extends Partial<PsychologicalAppealTypeModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
  prefix?: string
  addressBooks?: CommonAddressBooks
  formType?: string
}

export default class AppealItem extends ValidationElement<Props> {
  update = (queue: Partial<PsychologicalAppealTypeModel>) => {
    this.props.onUpdate({
      CourtName: this.props.CourtName,
      CourtAddress: this.props.CourtAddress,
      Disposition: this.props.Disposition,
      Comments: this.props.Comments,
      ...queue,
    });
  };

  updateCourtName = (values: PsychologicalAppealTypeModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtAddress = (values: PsychologicalAppealTypeModel['CourtAddress']) => {
    this.update({
      CourtAddress: values,
    });
  };

  updateDisposition = (values: PsychologicalAppealTypeModel['Disposition']) => {
    this.update({
      Disposition: values,
    });
  };

  updateComments = (values: PsychologicalAppealTypeModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render() {
    const prefix = this.props.prefix;
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="appeal">
        <Field
          title={i18n.t(`psychological.pvq.${prefix}.heading.appealCourtName`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <Text
            {...this.props.CourtName}
            name="CourtName"
            className="appealcourtname"
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtName`}
          />
        </Field>

        <Field
          attribute={`${attribute}.CourtAddress`}
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          dataTestId="orderAddress"
        >
          <PVQLocation
            name="CourtAddress"
            attribute={`${attribute}.CourtAddress`}
            {...this.props.CourtAddress}
            classname="appealcourtaddress"
            label={i18n.t(`psychological.pvq.${prefix}.label.appealCourtAddress`)}
            domesticTitle={i18n.t(`psychological.pvq.${prefix}.label.courtAddressDomesticTitle`)}
            foreignTitle={i18n.t(`psychological.pvq.${prefix}.label.courtAddressForeignTitle`)}
            includeMilitaryStates
            layout={Location.ADDRESS}
            geocode
            addressBook="Court"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateCourtAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            showMilitaryInstallation={false}
            showInternationalStreet={false}
          />
        </Field>

        <Field
          title={i18n.t(`psychological.pvq.${prefix}.heading.disposition`)}
          help={`psychological.${prefix}.help.disposition`}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Disposition`}
        >
          <Text
            {...this.props.Disposition}
            name="Disposition"
            className="disposition"
            onUpdate={this.updateDisposition}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Disposition`}
          />
        </Field>
      </div>

    ) : (
      <div className="appeal">
        <Field
          title={i18n.t(`psychological.${prefix}.heading.appealCourtName`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <Text
            {...this.props.CourtName}
            name="CourtName"
            className="appealcourtname"
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtName`}
          />
        </Field>

        <Field
          title={i18n.t(`psychological.${prefix}.heading.appealCourtAddress`)}
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtAddress`}
        >
          <Location
            {...this.props.CourtAddress}
            name="CourtAddress"
            className="appealcourtaddress"
            label={i18n.t(`psychological.${prefix}.label.courtAddress`)}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Court"
            showPostOffice
            onUpdate={this.updateCourtAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            attribute={`${attribute}.CourtAddress`}
          />
        </Field>

        <Field
          title={i18n.t(`psychological.${prefix}.heading.disposition`)}
          help={`psychological.${prefix}.help.disposition`}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Disposition`}
        >
          <Text
            {...this.props.Disposition}
            name="Disposition"
            className="disposition"
            onUpdate={this.updateDisposition}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Disposition`}
          />
        </Field>
        <Comments
          title={i18n.t(`psychological.${prefix}.comments`)}
          name="Comments"
          attribute="Comments"
          className="optional-comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          onError={this.props.onError}
          required={false}
        />
      </div>
    );
  }
}

AppealItem.defaultProps = {
  prefix: 'order',
  addressBooks: {},
  CourtName: {},
  CourtAddress: {},
  Disposition: {},
  Comments: {},
  formType: '',
  dispatch: (action) => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
